import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Editor from '@monaco-editor/react';
import { useAuth, ProgrammingLanguage } from '../contexts/FirebaseAuthContext';

interface FullscreenCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string | undefined) => void;
  solutionType: "code" | "algorithm" | "drawing" | "notes";
  problemTitle: string;
}

const getMonacoLanguage = (language: ProgrammingLanguage): string => {
  const mapping = {
    'python': 'python',
    'typescript': 'typescript',
    'java': 'java',
    'golang': 'go'
  };
  return mapping[language] || 'python';
};

export const FullscreenCodeModal: React.FC<FullscreenCodeModalProps> = ({
  isOpen,
  onClose,
  value,
  onChange,
  solutionType,
  problemTitle,
}) => {
  const { userProfile } = useAuth();
  // Memoize event handlers to prevent re-renders
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleModalClick = useCallback((e: React.MouseEvent) => {
    // Prevent clicks inside the modal from bubbling to the backdrop
    e.stopPropagation();
  }, []);

  // Prevent body scroll and hide floating elements when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('fullscreen-modal-open');
      return () => {
        document.body.style.overflow = 'unset';
        document.body.classList.remove('fullscreen-modal-open');
      };
    }
  }, [isOpen]);

  // Add global escape key listener
  useEffect(() => {
    if (!isOpen) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-75 z-[9999] fullscreen-code-modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div className="bg-white w-full h-full flex flex-col fullscreen-code-modal" onClick={handleModalClick}>
        <div className="fullscreen-code-header p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xl">
              {solutionType === "code" ? "💻" : solutionType === "notes" ? "📓" : "📝"}
            </span>
            <span className="font-semibold text-gray-900">
              {solutionType === "code" ? "Code Solution" : solutionType === "notes" ? "Notes" : "Algorithm Description"} - {problemTitle}
            </span>
          </div>
          <button
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
            onClick={onClose}
            aria-label="Close fullscreen editor"
            title="Close fullscreen editor (Esc)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="fullscreen-code-content flex-1 min-h-0 overflow-hidden">
          {solutionType === "code" ? (
            <Editor
              height="100%"
              defaultLanguage={getMonacoLanguage(userProfile?.preferredLanguage || 'python')}
              theme="vs-dark"
              value={value}
              onChange={onChange}
              options={{
                minimap: { enabled: true },
                fontSize: 16,
                lineNumbers: 'on',
                roundedSelection: false,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 4,
                insertSpaces: true,
                wordWrap: 'on',
                wrappingIndent: 'indent',
                suggestOnTriggerCharacters: true,
                acceptSuggestionOnEnter: 'on',
                tabCompletion: 'on',
                folding: true,
                renderWhitespace: 'selection',
                scrollbar: {
                  vertical: 'auto',
                  horizontal: 'auto',
                  verticalScrollbarSize: 14,
                  horizontalScrollbarSize: 14,
                },
                rulers: [80, 120],
                bracketPairColorization: {
                  enabled: true,
                },
                cursorBlinking: 'blink',
                cursorSmoothCaretAnimation: 'on',
              }}
              loading={<div className="flex items-center justify-center h-full text-gray-600">Loading fullscreen code editor...</div>}
            />
          ) : (
            <textarea
              className="w-full h-full p-4 border-none outline-none resize-none font-mono text-sm"
              placeholder={solutionType === "notes" ? "Write your notes, observations, or key learnings here..." : "Describe your algorithm approach here..."}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              autoFocus
            />
          )}
        </div>

        <div className="fullscreen-code-footer p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <span>💡 Tip: Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Esc</kbd> to close, <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Ctrl+/</kbd> for comments</span>
          </div>
          <button
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};